import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBuilding } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../../schoolCockpit/master/academicConfiguration/Academiclist.module.css";
// import payStyles from "./PayFees.module.css";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../components/PopupModal/PopupModal";
import AxiosInstance from "../../../services/Api";
import { buildPayFeesColumns } from "./payFeesColumns";
import PayFeesHistoryPanel from "./PayFeesHistoryPanel";
import usePayFeesData from "./usePayFeesData";

const PayFeesList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const labels = useMemo(() => translations?.labels || {}, [translations]);

  const [modalState, setModalState] = useState({
    open: false,
    type: "error",
    message: "",
  });
  // Blocks repeated clicks on the download icon until the in-flight
  // challan download finishes (fetch + print/save flow).
  const [downloadingId, setDownloadingId] = useState(null);

  const {
    data,
    loading,
    // error,
    rowCount,
    pagination,
    setPagination,
    searchInput,
    setSearchInput,
    handleSearchEnter,
    columnFilters,
    handleColumnFiltersChange,
  } = usePayFeesData();

  // Eye icon → navigate to the Pay Fees detail screen.
  const handleView = (rowData) => {
    navigate("/user/payfees", {
      state: {
        id: rowData?.fee_id,
        student_id: rowData?.student_id,
        admission_id: rowData?.admission_id,
      },
    });
  };

  // Download icon → download the challan PDF for this fees row.
  const handleDownload = async (rowData) => {
    const feeId = rowData?.fee_id;
    // Ignore clicks while a download is already in progress (same row
    // or another) so repeated clicks can't stack up multiple print jobs.
    if (!feeId || downloadingId) return;
    setDownloadingId(feeId);

    let iframe = null;
    const removeIframe = () => {
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
        iframe = null;
      }
      setDownloadingId(null);
    };

    try {
      const response = await AxiosInstance.get(
        `schoolFees/downloadChallan/${feeId}`,
        { responseType: "arraybuffer" },
      );
      if (!response?.data || response.data.byteLength === 0) {
        throw new Error("Empty challan response");
      }
      let html = new TextDecoder("utf-8").decode(response.data);

      html = html.replace(
        "font-size:12px; opacity:0.9;",
        "font-size:12px; opacity:0.9; font-weight:normal;",
      );

      const printCss =
        "<style>" +
        // margin: 0 stops Chrome reserving space for its own date/title/url/page-number
        // header & footer; the 12mm page margin is recreated as body padding instead.
        "@page { size: A4 portrait; margin: 0; }" +
        "body { margin: 0; padding: 12mm; box-sizing: border-box; }" +
        // force the coloured header background to actually print
        "html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }" +
        "</style>";
      const htmlToPrint = html.includes("</head>")
        ? html.replace("</head>", `${printCss}</head>`)
        : printCss + html;

      iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlToPrint);
      iframeDoc.close();

      // Wait for the iframe document to finish laying out before printing.
      await new Promise((resolve) => {
        if (iframeDoc.readyState === "complete") {
          resolve();
        } else {
          iframe.onload = () => resolve();
        }
        // hard fallback so we never hang if load never fires
        setTimeout(resolve, 1200);
      });

      const printWindow = iframe.contentWindow;
      // Clean up once the print dialog closes (Save or Cancel).
      printWindow.onafterprint = removeIframe;
      // Fallback cleanup in case onafterprint never fires.
      setTimeout(removeIframe, 60000);

      printWindow.focus();
      printWindow.print();
    } catch (err) {
      console.error("Error downloading challan:", err);
      removeIframe();
      setModalState({
        open: true,
        type: "error",
        message:
          labels.challanDownloadFailed ||
          "Could not download the challan. Please try again.",
      });
    }
  };

  const columns = useMemo(
    () =>
      buildPayFeesColumns({
        translations,
        onView: handleView,
        onDownload: handleDownload,
        isDownloading: downloadingId !== null,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations, downloadingId],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaBuilding />
        </div>
        <h2 className={styles.headerTitle}>{labels.payFees || "Pay Fees"}</h2>
      </div>

      {/* {error && !loading && <div className={payStyles.subHeading}>{error}</div>} */}

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        placeholder={labels.search || "Search"}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableSearch={false}
        enableExpanding={true}
        renderDetailPanel={({ row }) => <PayFeesHistoryPanel row={row} />}
        localization={{
          noRecordsToDisplay: labels.noRecordsFound || "No records found",
        }}
      />

      <PopupModal
        open={modalState.open}
        title={labels.failed || "Failed"}
        message={modalState.message}
        onOk={() => setModalState((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default PayFeesList;
